import { useCallback, useEffect, useRef, useState } from "react";

import {
  useGetCapabilityReadinessQuery,
  useProbeCapabilityReadinessMutation,
} from "./capabilityReadinessApi";
import type { CapabilityReadinessResponse } from "./capabilityReadinessSchemas";

export function capabilityReadinessAutoProbeKey(input: {
  organizationId: string;
  payload?: CapabilityReadinessResponse;
  shopBindingId: string;
}) {
  if (!input.organizationId || !input.shopBindingId || !input.payload) return null;
  const canRetry = input.payload.items.some(
    (candidate) =>
      candidate.shop_binding_id === input.shopBindingId &&
      candidate.actions.some((action) => action.kind === "retry_probe"),
  );
  if (!canRetry) return null;
  return `${input.organizationId}:${input.shopBindingId}:${input.payload.scope_version}`;
}

export function capabilityReadinessProbePollMatches(input: {
  currentOrganizationId: string;
  currentShopBindingId: string;
  startedOrganizationId: string;
  startedShopBindingId: string;
}) {
  return (
    input.currentOrganizationId === input.startedOrganizationId &&
    input.currentShopBindingId === input.startedShopBindingId
  );
}

export function useCapabilityReadinessProbe(
  organizationId: string,
  shopBindingId = "",
  options: { auto?: boolean } = {},
) {
  const query = useGetCapabilityReadinessQuery(
    { organizationId, shopBindingId },
    {
      pollingInterval: 30_000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      skip: !organizationId,
      skipPollingIfUnfocused: true,
    },
  );
  const [probe, probeState] = useProbeCapabilityReadinessMutation();
  const scopeKey = `${organizationId}:${shopBindingId}`;
  const [activeProbeKey, setActiveProbeKey] = useState<string | null>(null);
  const scopeRef = useRef({ organizationId, shopBindingId });
  const autoAttemptKeys = useRef(new Set<string>());
  const readbackGenerationRef = useRef(0);
  const refetch = query.refetch;

  useEffect(() => {
    const attemptKeys = autoAttemptKeys.current;
    const generation = readbackGenerationRef.current + 1;
    readbackGenerationRef.current = generation;
    scopeRef.current = { organizationId, shopBindingId };
    return () => {
      if (readbackGenerationRef.current === generation) {
        readbackGenerationRef.current += 1;
      }
      // StrictMode 的模拟卸载会紧接着重新执行 effect；清空本地键后，
      // 第二次仍可提交同一稳定幂等请求，而真实卸载不会继续 GET 追赶。
      attemptKeys.clear();
    };
  }, [organizationId, shopBindingId]);

  const runProbe = useCallback(
    async (
      nextShopBindingId: string,
      mode: "auto" | "manual",
      expectedScopeVersion?: string,
    ) => {
      if (!organizationId || !nextShopBindingId) return;
      const startedKey = `${organizationId}:${nextShopBindingId}`;
      const generation = readbackGenerationRef.current;
      setActiveProbeKey(startedKey);
      try {
        await probe({
          expectedScopeVersion,
          mode,
          organizationId,
          shopBindingId: nextShopBindingId,
        }).unwrap();
        const deadline = Date.now() + 20_000;
        while (Date.now() < deadline) {
          if (
            generation !== readbackGenerationRef.current ||
            !capabilityReadinessProbePollMatches({
              currentOrganizationId: scopeRef.current.organizationId,
              currentShopBindingId: scopeRef.current.shopBindingId,
              startedOrganizationId: organizationId,
              startedShopBindingId: nextShopBindingId,
            })
          ) {
            return;
          }
          const result = await refetch();
          if (generation !== readbackGenerationRef.current) return;
          if (
            mode === "auto" &&
            (result.data?.scope_version !== expectedScopeVersion ||
              capabilityReadinessAutoProbeKey({
                organizationId,
                payload: result.data,
                shopBindingId: nextShopBindingId,
              }) === null)
          ) {
            return;
          }
          await new Promise((resolve) => window.setTimeout(resolve, 1_500));
        }
      } finally {
        if (generation === readbackGenerationRef.current) {
          setActiveProbeKey((current) =>
            current === startedKey ? null : current,
          );
        }
      }
    },
    [organizationId, probe, refetch],
  );

  const retry = useCallback(
    (nextShopBindingId = shopBindingId) =>
      runProbe(nextShopBindingId, "manual"),
    [runProbe, shopBindingId],
  );

  useEffect(() => {
    if (!options.auto) return;
    const autoKey = capabilityReadinessAutoProbeKey({
      organizationId,
      payload: query.currentData,
      shopBindingId,
    });
    if (!autoKey || autoAttemptKeys.current.has(autoKey)) return;
    autoAttemptKeys.current.add(autoKey);
    void runProbe(
      shopBindingId,
      "auto",
      query.currentData?.scope_version,
    ).catch(() => undefined);
  }, [options.auto, organizationId, query.currentData, runProbe, shopBindingId]);

  return {
    probing: probeState.isLoading || activeProbeKey === scopeKey,
    query,
    retry,
  };
}
